import { supabase } from "@/lib/supabase";
import { Draft } from "@/types/draft";
import { mapSupabaseTrackToDraft } from "./upload/utils/track-mapper";

/**
 * Version families for the drafts list.
 *
 * The drafts query fetches MAIN WORKS ONLY (`parent_track_id IS NULL`). That alone
 * removes 7,865 rows from a 40,370-draft list — stems, instrumentals and 30-second
 * cuts of a parent that is already on screen.
 *
 * Children are then fetched once per page of parents, not once per row: a page of 30
 * parents costs exactly one extra query. The rows come back with the page, so expanding
 * a family is instant and needs no spinner.
 *
 * The hierarchy is exactly two levels deep — no version points at another version — so
 * this never recurses.
 */

/** Supabase caps `.in()` payloads; page size is 30, so this is headroom, not a limit. */
const MAX_PARENTS_PER_QUERY = 200;

export interface VersionChildren {
  /** parent track id -> its version rows, ordered for display */
  byParent: Map<string, Draft[]>;
  /** parent track id -> child count (present even when byParent has no entry) */
  counts: Map<string, number>;
  /**
   * Did every query answer?
   *
   * An empty map means two different things — a family with no versions, and a family whose read
   * failed — and a caller that overwrites a loaded family cannot tell them apart without this. A
   * page of the list may still render what it got (a missing badge beats no list); a refresh of
   * ONE family must not, or a single 503 blanks a badge that `getFamily` is at the same moment
   * answering "3" for.
   */
  ok: boolean;
}

/** A read that failed: no rows, and nothing that may be written over what is on screen. */
export const EMPTY_VERSIONS: VersionChildren = {
  byParent: new Map(),
  counts: new Map(),
  ok: false,
};

/**
 * Order versions the way a person reads a family: the full-length original first,
 * then progressively shorter edits. Falls back to title so the order is stable.
 */
const EDIT_RANK: Record<string, number> = {
  "Full Length": 0,
  "60 Second": 1,
  "30 Second": 2,
  "20 Second": 3,
  "15 Second": 4,
  "10 Second": 5,
  Ident: 6,
  Stab: 7,
};

function compareVersions(a: Draft, b: Draft): number {
  const ra = EDIT_RANK[a.edit_type ?? ""] ?? 50;
  const rb = EDIT_RANK[b.edit_type ?? ""] ?? 50;
  if (ra !== rb) return ra - rb;
  const va = (a.version_type ?? "").localeCompare(b.version_type ?? "");
  if (va !== 0) return va;
  return (a.title ?? "").localeCompare(b.title ?? "");
}

/**
 * Fetch every version belonging to the given parents.
 *
 * Label scoping is inherited from the parents: a child is only ever returned when its
 * parent id was passed in, and the parent list is already label-scoped by the caller.
 * The query is additionally constrained to unpublished rows so a published version is
 * never surfaced inside the Drafts surface.
 *
 * Never throws — a failure here must degrade the family badge, never blank the drafts list — so
 * it reports one in `ok` instead, and a caller that would overwrite good data checks it.
 */
export async function fetchVersionsForParents(
  parentIds: string[]
): Promise<VersionChildren> {
  const ids = [...new Set(parentIds.filter(Boolean))];
  // Nothing to read is not a failure: the answer for no parents is genuinely no children.
  if (ids.length === 0) return { byParent: new Map(), counts: new Map(), ok: true };

  const byParent = new Map<string, Draft[]>();
  const counts = new Map<string, number>();
  let ok = true;

  try {
    for (let i = 0; i < ids.length; i += MAX_PARENTS_PER_QUERY) {
      const slice = ids.slice(i, i + MAX_PARENTS_PER_QUERY);
      const { data, error } = await supabase
        .from("tracks")
        .select("*")
        .in("parent_track_id", slice)
        .eq("is_published", false)
        .order("parent_track_id", { ascending: true })
        .order("title", { ascending: true });

      if (error) {
        console.error("Failed to fetch draft versions:", error);
        ok = false;
        continue; // partial families beat no list at all
      }

      for (const row of data ?? []) {
        const parentId = row.parent_track_id as string;
        if (!parentId) continue;
        // The mapper is async only because of signed cover-art URLs; versions are
        // rendered as text rows, so we map them without awaiting artwork.
        const child = await mapSupabaseTrackToDraft(row, new Map());
        const bucket = byParent.get(parentId);
        if (bucket) bucket.push(child);
        else byParent.set(parentId, [child]);
      }
    }

    for (const [parentId, children] of byParent) {
      children.sort(compareVersions);
      counts.set(parentId, children.length);
    }
  } catch (err) {
    console.error("Unexpected error fetching draft versions:", err);
    return EMPTY_VERSIONS;
  }

  return { byParent, counts, ok };
}

/**
 * Attach fetched children to their parents. Parents with no versions get
 * `version_count: 0` so the UI can distinguish "no versions" from "not loaded yet".
 */
export function attachVersions(
  parents: Draft[],
  versions: VersionChildren
): Draft[] {
  return parents.map((parent) => {
    const children = versions.byParent.get(parent.id);
    return {
      ...parent,
      version_count: versions.counts.get(parent.id) ?? 0,
      versions: children ?? [],
    };
  });
}
